import { z } from 'zod';

import type { ConversationItem, ConversationItemsResponse } from './aiChatTypes';

// Drops a field that is null or of the wrong type instead of rejecting the whole item.
const optional = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

// Only the fields the app reads from history; the others are stripped.
const contentSchema = z
  .object({
    text: optional(z.string()),
    callId: optional(z.string()),
    // The gateway's spelling. Stored as `callId`, the name the streaming path uses.
    call_id: optional(z.string()),
    name: optional(z.string()),
    // The gateway stores tool output as a JSON string.
    output: optional(z.string()),
    metadata: optional(
      z.object({
        relatedQuestions: optional(z.array(z.string())),
        legalDisclaimer: optional(z.boolean()),
        title: optional(z.string()),
      }),
    ),
  })
  .transform(({ call_id: gatewayCallId, ...content }) => {
    const callId = content.callId ?? gatewayCallId;
    return callId === undefined ? content : { ...content, callId };
  });

const itemSchema = z
  .object({
    id: optional(z.string()),
    role: z.string(),
    type: optional(z.string()),
    content: optional(contentSchema),
    createdAt: optional(z.string()),
  })
  .passthrough();

const pageSchema = z.object({
  items: z.array(z.unknown()),
  nextPage: z.string().nullish().catch(null),
});

/**
 * Reads a page of conversation items from the LLM gateway. Items it can't map, such as reasoning,
 * come with no id or content, and a message whose first part isn't text comes with no content;
 * both stay in the page without those fields. Entries that aren't items at all are dropped and
 * reported. Throws when the page has no items list.
 */
export const parseConversationItemsResponse = (data: unknown): ConversationItemsResponse => {
  const page = pageSchema.safeParse(data);
  if (!page.success) {
    throw new Error('Conversation items response has no items list');
  }

  const items: ConversationItem[] = [];
  const issues: string[] = [];
  page.data.items.forEach((entry, index) => {
    const result = itemSchema.safeParse(entry);
    if (result.success) {
      items.push(result.data);
    } else {
      issues.push(...result.error.issues.map((issue) => `${[index, ...issue.path].join('.')}: ${issue.code}`));
    }
  });
  if (issues.length > 0) {
    // Paths and codes only: the items hold the user's conversation.
    assert(false, 'Dropped malformed AI chat history items', { issues });
  }

  return { items, nextPage: page.data.nextPage };
};
